import type { ReactElement, ReactNode } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getContainer } from '../../utils/generalFunctions/common';

import modalStyles from './Modal.module.css';
import styles from './ModalStack.module.css';

/** Maximum number of stacked modals; `push` throws past this. */
export const MAX_STACK_SIZE = 10;

/** One entry in the modal stack. */
export type ModalStackItem = {
  /** Stable id (required). */
  id: string;
  /** Renders the panel content; receives its stack index. Return a `Modal.Panel`. */
  render: (index: number) => ReactNode;
  /** Called when this entry leaves the stack (pop or clear). */
  onExit?: () => void;
};

type ModalStackContextValue = {
  /** Bottom-to-top order (last item is on top). */
  stack: ModalStackItem[];
  /** Push a modal on top of the stack. Throws once `MAX_STACK_SIZE` is reached. */
  push: (item: ModalStackItem) => void;
  /** Remove the top `count` modals (default 1). */
  pop: (count?: number) => void;
  /** Close the whole stack. */
  clear: () => void;
};

const ModalStackContext = createContext<ModalStackContextValue | null>(null);

/**
 * Access the modal stack. Must be used inside a `ModalStackProvider`.
 * `render(index)` receives the entry's stack index.
 */
export function useModalStack(): ModalStackContextValue {
  const context = useContext(ModalStackContext);
  if (!context) {
    throw new Error('useModalStack must be used within a <ModalStackProvider>');
  }
  return context;
}

type ModalStackProviderProps = {
  children: ReactNode;
  /** Portal target for the stack. Defaults to the app's Rango container. */
  container?: HTMLElement | null;
};

/**
 * Provides the modal stack and renders it. Each entry becomes a peeked panel;
 * one shared Radix Dialog owns the backdrop, scroll-lock, focus trap and Escape.
 * Wrap any subtree whose components need `useModalStack`.
 */
export function ModalStackProvider(props: ModalStackProviderProps): ReactElement {
  const [stack, setStack] = useState<ModalStackItem[]>([]);

  const push = useCallback(
    (item: ModalStackItem) => {
      if (stack.length >= MAX_STACK_SIZE) {
        throw new Error('Stack size exceeded');
      }
      setStack((previous) => previous.concat(item));
    },
    [stack],
  );

  const pop = useCallback((count = 1) => {
    setStack((previous) => {
      const removeFrom = Math.max(0, previous.length - count);
      previous.slice(removeFrom).forEach((item) => item.onExit?.());
      return previous.slice(0, removeFrom);
    });
  }, []);

  const clear = useCallback(() => {
    setStack((previous) => {
      previous.forEach((item) => item.onExit?.());
      return [];
    });
  }, []);

  const value = useMemo(() => ({ stack, push, pop, clear }), [stack, push, pop, clear]);

  return (
    <ModalStackContext.Provider value={value}>
      {props.children}
      <ModalStackViewport container={props.container} />
    </ModalStackContext.Provider>
  );
}

type ModalStackViewportProps = {
  container?: HTMLElement | null;
};

/** Rendered entry — kept mounted while `leaving` so it can animate out. */
type ModalStackEntry = {
  item: ModalStackItem;
  leaving: boolean;
};

/*
 * Fallback removal for a leaving panel, in case its exit `animationend` never
 * fires (e.g. prefers-reduced-motion disables the animation).
 */
const LEAVE_FALLBACK_MS = 400;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function ModalStackViewport(props: ModalStackViewportProps): ReactElement {
  const stackContext = useModalStack();
  const [entries, setEntries] = useState<ModalStackEntry[]>([]);

  /*
   * Reconcile the logical stack into render entries: present items first (in
   * stack order), then any items that have left the stack, marked `leaving` so
   * they stay mounted on top and animate out before being removed.
   */
  useEffect(() => {
    setEntries((previous) => {
      const stackIds = new Set(stackContext.stack.map((item) => item.id));
      const leaving = previous
        .filter((entry) => !stackIds.has(entry.item.id))
        .map((entry) => ({ item: entry.item, leaving: true }));
      const present = stackContext.stack.map((item) => ({ item, leaving: false }));
      return present.concat(leaving);
    });
  }, [stackContext.stack]);

  const removeEntry = useCallback((id: string) => {
    setEntries((previous) => previous.filter((entry) => entry.item.id !== id));
  }, []);

  // Safety net so leaving panels are always removed even without an animation.
  useEffect(() => {
    const timers = entries
      .filter((entry) => entry.leaving)
      .map((entry) => setTimeout(() => removeEntry(entry.item.id), LEAVE_FALLBACK_MS));
    return () => timers.forEach(clearTimeout);
  }, [entries, removeEntry]);

  const open = entries.length > 0;
  const presentEntries = entries.filter((entry) => !entry.leaving);
  const presentCount = presentEntries.length;
  const topId = presentCount > 0 ? presentEntries[presentCount - 1].item.id : undefined;

  /*
   * Keep the top panel interactive/focused and make the panels behind it inert.
   * `inert` (rather than `aria-hidden`) removes them from focus and the a11y
   * tree without the "aria-hidden on a focused ancestor" warning. On open Radix
   * auto-focuses the first control; pushing/popping swaps the top panel without
   * re-firing that, so move focus to the new top's first focusable when it changes.
   */
  useEffect(() => {
    const container = props.container ?? getContainer();
    if (!container) {
      return;
    }
    container.querySelectorAll<HTMLElement>('[data-modal-panel-id]').forEach((panel) => {
      panel.inert = panel.getAttribute('data-modal-panel-id') !== topId;
    });
    if (!topId) {
      return;
    }
    const topPanel = container.querySelector<HTMLElement>(`[data-modal-panel-id="${topId}"]`);
    const focusable = topPanel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable ?? topPanel)?.focus();
  }, [topId, props.container]);

  const handleViewportClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Clicking the empty backdrop area (not a panel) closes the whole stack.
    if (event.target === event.currentTarget) {
      stackContext.clear();
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          stackContext.clear();
        }
      }}>
      <Dialog.Portal container={props.container ?? getContainer()}>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          asChild
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            // Escape steps back one level rather than closing everything.
            event.preventDefault();
            stackContext.pop();
          }}>
          <div className={styles.viewport} onClick={handleViewportClick}>
            {/*
             * Accessible name for the dialog — screen readers announce this when
             * the stack opens. Each panel renders its own visible heading, so it
             * stays a generic, visually-hidden label for the stack container.
             */}
            <Dialog.Title className={styles.srOnly}>Modal</Dialog.Title>
            {entries.map((entry, index) => {
              const depth = entry.leaving ? 0 : presentCount - 1 - index;
              return (
                <div
                  key={entry.item.id}
                  data-modal-panel-id={entry.item.id}
                  tabIndex={-1}
                  className={`${modalStyles.panel} ${styles.panel}`}
                  data-depth={Math.min(depth, 2)}
                  data-leaving={entry.leaving ? 'true' : undefined}
                  onAnimationEnd={(event) => {
                    if (entry.leaving && event.target === event.currentTarget) {
                      removeEntry(entry.item.id);
                    }
                  }}>
                  {entry.item.render(index)}
                </div>
              );
            })}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
